import { normalizeDecimalTyping } from '@core/utils/number-utils';

function isDecimalField(input: HTMLInputElement): boolean {
    return input.type === 'number'
        || input.inputMode === 'decimal'
        || input.dataset['decimalInput'] === 'true';
}

function activateDecimalField(input: HTMLInputElement): void {
    input.dataset['decimalInput'] = 'true';
    input.inputMode = 'decimal';
    if (input.type !== 'text') {
        input.type = 'text';
    }
}

function applyNormalizedValue(input: HTMLInputElement): void {
    const raw = input.value;
    const normalized = normalizeDecimalTyping(raw);
    if (normalized === raw) {
        return;
    }

    const start = input.selectionStart ?? raw.length;
    const end = input.selectionEnd ?? start;
    const delta = normalized.length - raw.length;
    input.value = normalized;
    const nextStart = Math.max(0, Math.min(normalized.length, start + delta));
    const nextEnd = Math.max(nextStart, Math.min(normalized.length, end + delta));
    input.setSelectionRange(nextStart, nextEnd);
}

function insertDecimalSeparator(input: HTMLInputElement): void {
    activateDecimalField(input);
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const selected = input.value.slice(start, end);
    const outside = input.value.slice(0, start) + input.value.slice(end);
    if (/[.,]/.test(outside) && !/[.,]/.test(selected)) {
        return;
    }

    const next = input.value.slice(0, start) + '.' + input.value.slice(end);
    input.value = normalizeDecimalTyping(next);
    const pos = Math.min(input.value.length, start + 1);
    input.setSelectionRange(pos, pos);
    input.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Accepts both "," and "." in every numeric field, including inputs created outside Angular (dialogs).
 * Number inputs are switched to text so the browser does not reject one of the two separators.
 */
export function installDecimalInputListener(): void {
    if (typeof document === 'undefined') {
        return;
    }

    document.addEventListener('focusin', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !isDecimalField(input)) {
            return;
        }

        activateDecimalField(input);
    }, true);

    document.addEventListener('keydown', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'number') {
            return;
        }

        if (event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }

        if (event.key !== ',' && event.key !== '.' && event.key !== 'Decimal') {
            return;
        }

        event.preventDefault();
        insertDecimalSeparator(input);
    }, true);

    document.addEventListener('input', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !isDecimalField(input)) {
            return;
        }

        activateDecimalField(input);
        applyNormalizedValue(input);
    }, true);
}
